import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subscription } from 'rxjs';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag } from '@angular/cdk/drag-drop';
import { FourMiniCubesIconComponent } from '@svg-icons/four-mini-cubes-icon/four-mini-cubes-icon.component';
import { PlusIconComponent } from '@svg-icons/plus-icon/plus-icon.component';
import { PinIconComponent } from '@svg-icons/pin-icon/pin-icon.component';
import { PinnedIconComponent } from '@svg-icons/pinned-icon/pinned-icon.component';
import { RtIconCloseComponent } from '@rt-icons/rt-icon-close/rt-icon-close.component';
import { NavMenuIconComponent } from '@ui-components/nav-menu-icon/nav-menu-icon.component';
import { AppGlobalService } from 'src/app/services/app.global.service';

export interface NavTabItem {
  id: string;
  label: string;
  icon?: string;
  route?: string;
  pinned?: boolean;
  group?: string;
}

export interface NavTabBarConfig {
  /** Enable pin/unpin functionality */
  enablePinning: boolean;
  /** Enable drag-drop reordering */
  enableDragDrop: boolean;
  /** Enable grouped menu headers */
  enableGrouping: boolean;
  /** localStorage key for pinned state persistence */
  storageKey: string;
  /** Route path for overview navigation */
  overviewRoute: string;
  /** Menu group ordering (only used when enableGrouping=true) */
  menuGroups?: string[];
  /** Render a full border on all sides (build); otherwise only border-bottom (operate) */
  fullBorder?: boolean;
}

const DEFAULT_CONFIG: NavTabBarConfig = {
  enablePinning: true,
  enableDragDrop: true,
  enableGrouping: true,
  storageKey: 'artifacts',
  overviewRoute: 'artifacts/overview',
  menuGroups: []
};

@Component({
  selector: 'app-nav-tab-bar',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FourMiniCubesIconComponent,
    PlusIconComponent,
    PinIconComponent,
    PinnedIconComponent,
    RtIconCloseComponent,
    NavMenuIconComponent,
    CdkDropList,
    CdkDrag
  ],
  templateUrl: './nav-tab-bar.component.html',
  styleUrls: ['./nav-tab-bar.component.scss']
})
export class NavTabBarComponent implements OnInit, OnDestroy {
  @Input() items: NavTabItem[] = [];
  @Input() config: NavTabBarConfig = DEFAULT_CONFIG;
  @Input() disabled = false;

  @Output() overviewClicked = new EventEmitter<void>();
  @Output() tabActivated = new EventEmitter<NavTabItem>();
  @Output() tabClosed = new EventEmitter<NavTabItem>();
  // Parents use this to keep their own route-memory or header state aligned
  // with the bar's active tab, including overview resets and pin changes.
  @Output() activeItemChange = new EventEmitter<NavTabItem | null>();

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly appGlobalService = inject(AppGlobalService);
  private routerSub!: Subscription;

  isMenuOpen = false;
  menuPositionClass: 'align-left' | 'align-right' = 'align-left';
  activeItem: NavTabItem | null = null;
  selectedItems: NavTabItem[] = [];
  orderedPinnedIds: string[] = [];
  isBuildPage = false;

  pinnedItems: NavTabItem[] = [];
  unpinnedMenuItems: NavTabItem[] = [];
  groupedMenuItems: { group: string; items: NavTabItem[] }[] = [];
  unpinnedSelectedItems: NavTabItem[] = [];

  ngOnInit(): void {
    this.isBuildPage = !!this.config.fullBorder;
    if (this.config.enablePinning) {
      this.loadPinnedState();
    }
    this.checkCurrentRoute(this.router.url);
    this.recomputeDerived();

    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.checkCurrentRoute(e.urlAfterRedirects);
        this.recomputeDerived();
        this.cdr.markForCheck();
      });

    if (this.config.enablePinning) {
      window.addEventListener('storage', this.onStorageChange);
    }
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    if (this.config.enablePinning) {
      window.removeEventListener('storage', this.onStorageChange);
    }
  }

  goToOverview(event: Event): void {
    event.stopPropagation();
    this.isMenuOpen = false;
    this.activeItem = null;
    this.cdr.markForCheck();
    this.overviewClicked.emit();
    this.activeItemChange.emit(null);
    void this.router.navigate([this.config.overviewRoute], { relativeTo: this.route });
  }

  handleTabClick(item: NavTabItem): void {
    this.activeItem = item;
    this.tabActivated.emit(item);
    this.activeItemChange.emit(item);
    if (item.route) {
      void this.router.navigate([item.route], { relativeTo: this.route });
    }
  }

  handleItemClick(item: NavTabItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMenuOpen = false;
    this.cdr.detectChanges();

    if (!this.selectedItems.find((i) => i.id === item.id)) {
      this.selectedItems.push(item);
    }
    this.activeItem = item;
    this.recomputeDerived();
    this.tabActivated.emit(item);
    this.activeItemChange.emit(item);

    if (item.route) {
      void this.router.navigate([item.route], { relativeTo: this.route }).then(() => {
        this.cdr.detectChanges();
      });
    }

    setTimeout(() => {
      const tabElement = document.getElementById(`tab-${item.id}`);
      if (tabElement) {
        tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }, 50);
  }

  closeTab(item: NavTabItem, event: MouseEvent): void {
    event.stopPropagation();
    this.selectedItems = this.selectedItems.filter((i) => i.id !== item.id);
    this.recomputeDerived();
    this.tabClosed.emit(item);

    if (this.activeItem?.id === item.id) {
      this.activeItem = null;
      this.goToOverview(event);
    }
  }

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;

    if (this.isMenuOpen && event.currentTarget) {
      const target = event.currentTarget as HTMLElement;
      const rect = target.getBoundingClientRect();
      const screenWidth = window.innerWidth;
      this.menuPositionClass = (screenWidth - rect.right < 270) ? 'align-right' : 'align-left';
    }
  }

  menuClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  togglePin(item: NavTabItem, event: MouseEvent): void {
    if (!this.config.enablePinning) { return; }
    event.stopPropagation();
    item.pinned = !item.pinned;

    if (!item.pinned && this.activeItem?.id !== item.id) {
      this.selectedItems = this.selectedItems.filter((i) => i.id !== item.id);
    }

    let pinnedState: string[] = this.appGlobalService.getLSAppInfo(this.config.storageKey, 'pinnedTabs') || [];

    if (item.pinned) {
      if (!pinnedState.includes(item.id)) {
        pinnedState.push(item.id);
      }
    } else {
      pinnedState = pinnedState.filter((id) => id !== item.id);
    }

    this.orderedPinnedIds = [...pinnedState];
    this.appGlobalService.saveLSAppInfo(this.config.storageKey, 'pinnedTabs', pinnedState);
    this.recomputeDerived();

    if (this.activeItem?.id === item.id) {
      this.activeItemChange.emit(this.activeItem);
    }

    this.cdr.markForCheck();
  }

  drop(event: CdkDragDrop<NavTabItem[]>): void {
    if (!this.config.enableDragDrop) { return; }
    const unpinned = this.unpinnedSelectedItems;
    moveItemInArray(unpinned, event.previousIndex, event.currentIndex);
    const pinned = this.pinnedItems;
    this.selectedItems = [...pinned, ...unpinned];
    this.recomputeDerived();
  }

  dropPinned(event: CdkDragDrop<string[]>): void {
    if (!this.config.enableDragDrop) { return; }
    moveItemInArray(this.orderedPinnedIds, event.previousIndex, event.currentIndex);
    this.appGlobalService.saveLSAppInfo(this.config.storageKey, 'pinnedTabs', this.orderedPinnedIds);
    this.recomputeDerived();
    this.cdr.markForCheck();
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isMenuOpen = false;
  }

  trackById(_index: number, item: NavTabItem): string {
    return item.id;
  }

  trackByGroup(_index: number, group: { group: string }): string {
    return group.group;
  }

  private recomputeDerived(): void {
    if (this.config.enablePinning) {
      this.pinnedItems = this.orderedPinnedIds
        .map((id) => this.items.find((i) => i.id === id))
        .filter((i) => !!i && i.pinned) as NavTabItem[];
      this.unpinnedMenuItems = this.items.filter((i) => !i.pinned);
    } else {
      this.pinnedItems = [];
      this.unpinnedMenuItems = this.items.filter(
        (i) => !this.selectedItems.some((s) => s.id === i.id)
      );
    }

    if (this.config.enableGrouping && this.config.menuGroups?.length) {
      const groups: { group: string; items: NavTabItem[] }[] = this.config.menuGroups
        .map((group) => ({
          group,
          items: this.unpinnedMenuItems.filter((i) => i.group === group)
        }))
        .filter((g) => g.items.length > 0);

      const ungrouped = this.unpinnedMenuItems.filter((i) => !i.group);
      if (ungrouped.length > 0) {
        groups.push({ group: 'OTHER', items: ungrouped });
      }
      this.groupedMenuItems = groups;
    } else {
      this.groupedMenuItems = [{ group: '', items: this.unpinnedMenuItems }];
    }

    this.unpinnedSelectedItems = this.selectedItems.filter((i) => !i.pinned);
  }

  private checkCurrentRoute(url: string): void {
    const foundItem = this.items.find((item) => item.route && url.includes(item.route));
    if (foundItem) {
      if (!this.selectedItems.find((i) => i.id === foundItem.id)) {
        this.selectedItems.push(foundItem);
      }
      this.activeItem = foundItem;
      this.activeItemChange.emit(foundItem);

      setTimeout(() => {
        const tabElement = document.getElementById(`tab-${foundItem.id}`);
        if (tabElement) {
          tabElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }, 50);
    } else if (url.includes(this.config.overviewRoute)) {
      this.activeItem = null;
      this.activeItemChange.emit(null);
    }
  }

  private loadPinnedState(): void {
    const pinnedIds: string[] = this.appGlobalService.getLSAppInfo(this.config.storageKey, 'pinnedTabs') || [];
    this.orderedPinnedIds = [...pinnedIds];

    this.items.forEach((item) => {
      const isNowPinned = pinnedIds.includes(item.id);
      if (item.pinned && !isNowPinned && this.activeItem?.id !== item.id) {
        this.selectedItems = this.selectedItems.filter((i) => i.id !== item.id);
      }
      item.pinned = isNowPinned;
    });

    this.recomputeDerived();
    this.cdr.markForCheck();
  }

  private onStorageChange = (event: StorageEvent): void => {
    if (event.key === 'appInfo') {
      this.loadPinnedState();
    }
  };
}
